/**
 * Sprint 12 — Coaching plan authoring panel.
 *
 * Three labeled textareas (strengths to build from / gaps to address /
 * practice plan), markdown supported, autosave on blur. Visible last-
 * saved indicator. Draft/published status with confirmation modal on
 * publish; revert to draft is a single click (no confirm — doesn't
 * affect what the coachee sees).
 *
 * Spec Part 5.2, 7.5, 9.
 */
import React from "react";
import { api, formatApiError } from "../lib/api";
import ConfirmModal from "./ConfirmModal";
import CoachNotesPanel from "./CoachNotesPanel";

const PLACEHOLDERS = {
  strengths_to_leverage:
    "Which specific high items will be the resource? What's the channel through which they get used?",
  gaps_to_address:
    "Which specific low items will be the focus? What practice addresses each?",
  practice_plan:
    "What practices, at what frequency, over what duration? Repetition and consistency matter more than insight.",
};

const SECTION_TITLES = {
  strengths_to_leverage: "Strengths to build from",
  gaps_to_address: "Gaps to address",
  practice_plan: "Practice plan",
};

function formatSavedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const t = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return `Saved ${t}`;
  } catch {
    return null;
  }
}

function PlanTextarea({ field, value, onBlur, disabled }) {
  const [draft, setDraft] = React.useState(value || "");
  // Sync external value updates (e.g. status flip re-fetches plan) into
  // local state when the user is NOT editing — guards against
  // clobbering in-flight edits.
  const focusedRef = React.useRef(false);
  React.useEffect(() => {
    if (!focusedRef.current) setDraft(value || "");
  }, [value]);

  return (
    <div data-testid={`plan-field-${field}`}>
      <label
        className="eyebrow"
        style={{ color: "#2F6B7C", display: "block", marginBottom: 6 }}
      >
        {SECTION_TITLES[field]}
      </label>
      <textarea
        value={draft}
        disabled={disabled}
        placeholder={PLACEHOLDERS[field]}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => {
          focusedRef.current = false;
          if ((draft || "") !== (value || "")) onBlur(draft);
        }}
        data-testid={`plan-textarea-${field}`}
        rows={5}
        style={{
          width: "100%",
          padding: "10px 12px",
          fontFamily: "Georgia, serif",
          fontSize: "14px",
          lineHeight: 1.6,
          color: "#1A1A1A",
          background: "white",
          border: "1px solid #E0E0E0",
          resize: "vertical",
        }}
      />
    </div>
  );
}

function ResourceAttachStrip({ resources, onAttach, disabled }) {
  const live = ["capacity", "focus", "time"].map((k) => resources?.[k]).filter((r) => r && !r.suppressed);
  if (live.length === 0) return null;
  const lowestKey = live.reduce((a, b) => (b.score < a.score ? b : a)).key;
  return (
    <div className="p-4" style={{ background: "white", border: "1px solid #E0E0E0" }} data-testid="plan-resource-strip">
      <div className="eyebrow mb-2" style={{ color: "#4592A8" }}>Anchor plan items to a resource</div>
      <div className="flex flex-wrap gap-3">
        {live.map((r) => (
          <div key={r.key} className="flex items-center gap-2 text-sm" data-testid={`plan-resource-${r.key}`}>
            <span style={{ fontFamily: "Oswald", fontWeight: 700 }}>{r.label} {r.score_display}</span>
            <span className="text-xs text-gray-500">{r.band}</span>
            {r.key === lowestKey && (
              <span className="text-xs px-2" style={{ background: "#FCE7E8", color: "#AD393E", fontFamily: "Oswald", letterSpacing: "0.08em" }} data-testid="plan-lowest-resource">
                Lowest resource
              </span>
            )}
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAttach(r)}
              className="text-xs underline"
              style={{ color: "#2F6B7C" }}
              data-testid={`plan-attach-${r.key}`}
            >
              Add to gaps
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CoachingPlanPanel({ respondentId, clientName, isCompleted = true, resources = null }) {
  const [plan, setPlan] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmPublish, setConfirmPublish] = React.useState(false);
  // 'last action' string for the visible save indicator. Independent
  // from plan.updated_at because the latter doesn't reflect a no-op
  // re-save when the user blurred unchanged.
  const [lastSavedIso, setLastSavedIso] = React.useState(null);

  React.useEffect(() => {
    if (!respondentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/respondents/${respondentId}/coaching-plan`);
        if (!cancelled) {
          setPlan(data.plan);
          setLastSavedIso(data.plan?.updated_at || null);
        }
      } catch (e) {
        // 404 just means "no plan yet" — show the empty form.
        if (e?.response?.status === 404) {
          if (!cancelled) setPlan(null);
        } else if (!cancelled) {
          setError(formatApiError(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [respondentId]);

  const saveField = async (field, value) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(
        `/api/respondents/${respondentId}/coaching-plan`,
        { [field]: value },
      );
      setPlan(data.plan);
      setLastSavedIso(data.plan?.updated_at || new Date().toISOString());
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const transition = async (newStatus) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(
        `/api/respondents/${respondentId}/coaching-plan/publish-status`,
        { status: newStatus },
      );
      setPlan(data.plan);
      setLastSavedIso(data.plan?.updated_at || new Date().toISOString());
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
      setConfirmPublish(false);
    }
  };

  if (!respondentId) return null;

  const status = plan?.status || "draft";
  const isPublished = status === "published";
  const publishedAt = plan?.published_at;
  const savedLabel = saving ? "Saving…" : formatSavedAt(lastSavedIso);

  return (
    <section data-testid="coaching-plan-panel" className="space-y-4 p-6"
             style={{ background: "#FBFAF7", border: "1px solid #E0E0E0" }}>
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <div className="eyebrow" style={{ color: "#2563EB" }}>Coaching plan</div>
          <h3 className="text-2xl"
              style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A", marginTop: 4 }}>
            Designed by you for {clientName || "this client"}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="coaching-plan-status-badge"
            className="text-xs uppercase tracking-widest px-3 py-1"
            style={{
              fontFamily: "Oswald",
              background: isPublished ? "#E2F3EC" : "#FAF7F1",
              color: isPublished ? "#1B8A6B" : "#7F7F7F",
            }}
          >
            {status}
          </span>
        </div>
      </header>

      {loading && <div className="text-sm text-gray-500">Loading plan…</div>}
      {error && (
        <div className="text-sm" style={{ color: "#AD393E" }} data-testid="coaching-plan-error">
          {error}
        </div>
      )}

      {!loading && (
        <>
          <ResourceAttachStrip
            resources={resources}
            disabled={saving}
            onAttach={(r) => {
              const lows = r.components.filter((c) => c.is_lowest).map((c) => c.label).join(" and ");
              const line = `**${r.label} ${r.score_display} (${r.band}) — lowest: ${lows}.** `;
              const current = plan?.gaps_to_address || "";
              saveField("gaps_to_address", current ? `${current}\n\n${line}` : line);
            }}
          />
          <div className="space-y-4">
            {["strengths_to_leverage", "gaps_to_address", "practice_plan"].map((field) => (
              <PlanTextarea
                key={field}
                field={field}
                value={plan?.[field] || ""}
                onBlur={(v) => saveField(field, v)}
                disabled={saving}
              />
            ))}
          </div>

          <footer className="flex items-center justify-between gap-3 pt-2">
            <div className="text-xs text-gray-500" data-testid="coaching-plan-save-indicator">
              {savedLabel || "—"}
              {publishedAt && (
                <span className="ml-3" data-testid="coaching-plan-published-at">
                  Published {new Date(publishedAt).toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isPublished ? (
                <button
                  type="button"
                  onClick={() => transition("draft")}
                  disabled={saving}
                  data-testid="coaching-plan-revert-btn"
                  className="btn-quiet"
                  style={{ fontFamily: "Oswald", letterSpacing: "0.18em",
                           textTransform: "uppercase", fontSize: "11px",
                           padding: "8px 14px", border: "1px solid #E0E0E0",
                           background: "white", cursor: saving ? "default" : "pointer" }}
                >
                  Revert to draft
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmPublish(true)}
                  disabled={saving}
                  data-testid="coaching-plan-publish-btn"
                  style={{ fontFamily: "Oswald", letterSpacing: "0.18em",
                           textTransform: "uppercase", fontSize: "11px",
                           padding: "8px 14px", background: "#1B8A6B",
                           color: "white", border: 0, cursor: saving ? "default" : "pointer" }}
                >
                  Publish to report
                </button>
              )}
            </div>
          </footer>
        </>
      )}

      <ConfirmModal
        open={confirmPublish}
        title="Publish coaching plan?"
        body={`This will include the plan in the next report sent to ${clientName || "this client"}.`}
        confirmLabel="Publish"
        cancelLabel="Cancel"
        onClose={() => setConfirmPublish(false)}
        onConfirm={() => transition("published")}
        testId="coaching-plan-publish-confirm"
      />

      {/*
        Coach Notes Library — coach-side reference content below the
        plan textareas. Layout choice per build-time user preference:
        coach composes the plan first, library is the look-up if
        stuck. Editorial-integrity guard in
        `backend/tests/test_coach_notes.py` enforces that this content
        never reaches the PDF or any client surface.
      */}
      <CoachNotesPanel respondentId={respondentId} isCompleted={isCompleted} />
    </section>
  );
}
